import {
  Button,
  Checkbox,
  Collapsible,
  Container,
  createListCollection,
  Field,
  Heading,
  Input,
  Text,
  Link,
  FieldRequiredIndicator,
  Table,
  Select,
  Portal,
  SimpleGrid,
  GridItem,
  DatePicker,
  parseDate,
  Flex,
} from "@chakra-ui/react"
import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { useEffect, useState } from "react"
import { getAGB, handleError } from "../../utils"
import useCustomToast from "../../hooks/useCustomToast"
import { Controller, SubmitHandler, useForm } from "react-hook-form"
import { ApiError, FormsSubscriptionCreate, FormsSubscriptionService } from "../../client"
import { PhoneNumberFormat, PhoneNumberUtil } from "google-libphonenumber"
import CityZipField from "../../components/Common/CitiesFields"
import { useReCaptcha } from "../../hooks/useReCaptcha"
import { getSettingsSubscriptions } from "../../api/subscriptions"
import { useSettings } from "../../components/UI/settings-context"
import NotFound from "../../components/Common/NotFound"
import { LuCalendar } from "react-icons/lu"
import { today } from "@internationalized/date"
import { Helmet } from "react-helmet-async"
const ENVIRONMENT = import.meta.env.VITE_ENVIRONMENT

export const Route = createFileRoute("/_layout/abo")({
  component: Subscription,
  notFoundComponent: NotFound,
})

function Subscription() {
  const [dsgChecked, setDsgChecked] = useState(false)
  const [open, setOpen] = useState(false)

  const [agbLink, setAgbLink] = useState<string | null>(null)
  const {data: agb} = useQuery({
    ...getAGB()
  })

  useEffect(() => {
    if(agb?.agb) {
      setAgbLink(agb.agb.path)
    }
  }, [agb])

  const policy = useSettings()?.policy

  const {data: settingsSubscriptions} = useQuery({
    ...getSettingsSubscriptions()
  })

  const siteKey = policy?.captcha_id ?? ""
  const { getToken } = useReCaptcha(siteKey, "subscription_form")

  const showToast = useCustomToast()

  const phoneUtil = PhoneNumberUtil.getInstance()
  const PNF = PhoneNumberFormat

  const {
    register,
    handleSubmit,
    reset,
    control,
    setValue,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormsSubscriptionCreate>({
    mode: "onBlur",
    criteriaMode: "all",
    defaultValues: {
      title: null,
      phone: null,
      billing_title: null,
      billing_firstname: null,
      billing_lastname: null,
      billing_address: null,
      billing_city: null,
      billing_postcode: null,
    },
  })

  const mutation = useMutation({
    mutationFn: (data: { token: string; body: FormsSubscriptionCreate }) =>
      FormsSubscriptionService.createFormsSubscription({ recaptchaToken: data.token, requestBody: data.body }),
    onSuccess: () => {
      showToast("Versendet!", "Aboanfrage erfolgreich gesendet", "success")
      setOpen(false)
      reset()
    },
    onError: (err: ApiError) => {
      handleError(err, showToast)
    },
  })

  const onSubmit: SubmitHandler<FormsSubscriptionCreate> = async (data) => {
    if(data.phone != null && data.phone != undefined && data.phone != "") {
      const number = phoneUtil.parseAndKeepRawInput(data.phone, "CH")
      data.phone = phoneUtil.format(number, PNF.RFC3966)
    } else data.phone = null

    const isTestEnv = ["local", "CI"].includes(ENVIRONMENT)

    // reCAPTCHA
    let token: string | null = "test-token"

    if (!isTestEnv) {
        token = await getToken()
        if(!token) {
            showToast("Fehler!", "reCAPTCHA konnte nicht ausgeführt werden", "error")
            return
        }
    }

    mutation.mutate({ token, body: data })
  }

  // Lists
  const aboTypes = createListCollection({
    items: [
      { label: "Schweiz Jahr", value: "Schweiz Jahr" },
      { label: "Schweiz Halbjahr", value: "Schweiz Halbjahr" },
      { label: "Schweiz Vierteljahr", value: "Schweiz Vierteljahr" },
      { label: "Europa Jahr", value: "Europa Jahr" },
      { label: "Übersee Jahr", value: "Übersee Jahr" },
    ],
  })

  const titles = createListCollection({
    items: [
      { label: "Frau", value: "Frau" },
      { label: "Herr", value: "Herr" },
      { label: "Keine Angabe", value: "Keine Angabe" },
    ],
  })

  // Validate phone numbers
  const isValidNumber = (value: any) => {
    if (value && value.length >= 10) {
      const number = phoneUtil.parseAndKeepRawInput(value, "CH")
      const res = phoneUtil.isValidNumberForRegion(number, "CH")
      return res
    }
    return true
  }

  return (
    <>
      <Helmet>
        {policy?.captcha_id && <script src={`https://www.google.com/recaptcha/api.js?render=${policy.captcha_id}`} /> }
      </Helmet>

      <Container>
      <Heading color="main" size="4xl">Abo der Wochen-Zeitung bestellen</Heading>

        {/* Subscription prices */}
        <Table.Root size="md" my={16}>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader colSpan={1}></Table.ColumnHeader>

              <Table.ColumnHeader textTransform="uppercase">
                {settingsSubscriptions?.price_ch_year ||
                settingsSubscriptions?.price_europe ||
                settingsSubscriptions?.price_overseas
                ? "Jahr" : ""}
              </Table.ColumnHeader>

              <Table.ColumnHeader textTransform="uppercase">{settingsSubscriptions?.price_ch_half_year ? "Halbjahr" : ""}</Table.ColumnHeader>

              <Table.ColumnHeader textTransform="uppercase" textAlign="end">{settingsSubscriptions?.price_ch_quarter ? "Vierteljahr" : ""}</Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
              <Table.Row key="key-swiss">
                <Table.Cell textTransform="uppercase">
                  {settingsSubscriptions?.price_ch_year ||
                  settingsSubscriptions?.price_ch_half_year ||
                  settingsSubscriptions?.price_ch_quarter
                  ? "Schweiz" : ""}
                </Table.Cell>

                <Table.Cell fontWeight="light">{settingsSubscriptions?.price_ch_year ? `CHF ${settingsSubscriptions?.price_ch_year}` : ""}</Table.Cell>

                <Table.Cell fontWeight="light">{settingsSubscriptions?.price_ch_half_year ? `CHF ${settingsSubscriptions?.price_ch_half_year}` : ""}</Table.Cell>

                <Table.Cell textAlign="end" fontWeight="light">{settingsSubscriptions?.price_ch_quarter ? `CHF ${settingsSubscriptions?.price_ch_quarter}` : ""}</Table.Cell>
              </Table.Row>

              {settingsSubscriptions?.price_europe &&
                <Table.Row key="key-europe">
                  <Table.Cell textTransform="uppercase">Europa</Table.Cell>
                  <Table.Cell fontWeight="light">CHF {settingsSubscriptions.price_europe}</Table.Cell>
                  <Table.Cell colSpan={2}></Table.Cell>
                </Table.Row>
              }

              {settingsSubscriptions?.price_overseas &&
                <Table.Row key="key-oversea">
                  <Table.Cell textTransform="uppercase">Übersee</Table.Cell>
                  <Table.Cell fontWeight="light">CHF {settingsSubscriptions.price_overseas}</Table.Cell>
                  <Table.Cell colSpan={2}></Table.Cell>
                </Table.Row>
              }
          </Table.Body>
          {settingsSubscriptions?.vat &&
            <Table.Caption textAlign="start" mt={4}>Preisangaben inkl. {String(Math.round(Number(settingsSubscriptions.vat) * 10) / 10)}% MwSt.</Table.Caption>
          }
          <Table.Caption textAlign="start" mt={4}>Im Verteilgebiet erhalten Sie die Wochen-Zeitung gratis</Table.Caption>
        </Table.Root>

        {/* Subscription form */}
        <form onSubmit={handleSubmit(onSubmit)}>
          <SimpleGrid gap={5} columns={2}>
            <GridItem colSpan={{base: 2, md: 1}}>
              <Field.Root required invalid={!!errors.type}>
                <Field.Label htmlFor="type">
                  Typ
                  <FieldRequiredIndicator />
                </Field.Label>

                <Controller
                  control={control}
                  name="type"
                  render={({ field }) => (
                    <Select.Root
                      name={field.name}
                      onValueChange={({ value }) => field.onChange(value[0])}
                      onInteractOutside={() => field.onBlur()}
                      collection={aboTypes}
                      w="full"
                      deselectable
                    >
                      <Select.HiddenSelect />
                      <Select.Control>
                        <Select.Trigger>
                          <Select.ValueText placeholder="Abo typ" />
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>
                      <Select.Positioner>
                        <Select.Content>
                          {aboTypes.size > 0 ? (
                            aboTypes.items.map((aboType) => (
                              <Select.Item item={aboType} key={aboType.value}>
                                {aboType.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))
                          ): (
                            <Text>Keine Abo-Typen vorhanden</Text>
                          )}
                        </Select.Content>
                      </Select.Positioner>
                    </Select.Root>
                  )}
                />

                {errors.type && (
                  <Field.ErrorText>{errors.type.message}</Field.ErrorText>
                )}
              </Field.Root>
            </GridItem>

            <GridItem colSpan={{base: 2, md: 1}}>
              <Field.Root invalid={!!errors.subscription_start_date} required>
                <Field.Label htmlFor="subscription_start_date">
                  Startdatum
                  <Field.RequiredIndicator />
                </Field.Label>

                <Controller
                  control={control}
                  name="subscription_start_date"
                  rules={{ required: "Startdatum ist erforderlich" }}
                  render={({ field }) => (
                    <DatePicker.Root
                      value={field.value ? [parseDate(field.value)] : []}
                      onValueChange={(e) =>
                        field.onChange(e.value[0]?.toString() ?? "")
                      }
                      invalid={!!errors.subscription_start_date}
                      locale="de-CH"
                      min={today("Europe/Zurich")}
                    >
                      <DatePicker.Control>
                        <DatePicker.Input placeholder="Startdatum" />
                        <DatePicker.IndicatorGroup>
                          <DatePicker.Trigger>
                            <LuCalendar />
                          </DatePicker.Trigger>
                        </DatePicker.IndicatorGroup>
                      </DatePicker.Control>

                      <Portal>
                        <DatePicker.Positioner>
                          <DatePicker.Content>
                            <DatePicker.View view="day">
                              <DatePicker.Header />
                              <DatePicker.DayTable />
                            </DatePicker.View>
                            <DatePicker.View view="month">
                              <DatePicker.Header />
                              <DatePicker.MonthTable />
                            </DatePicker.View>
                            <DatePicker.View view="year">
                              <DatePicker.Header />
                              <DatePicker.YearTable />
                            </DatePicker.View>
                          </DatePicker.Content>
                        </DatePicker.Positioner>
                      </Portal>
                    </DatePicker.Root>
                  )}
                />

                {errors.subscription_start_date && (
                  <Field.ErrorText>{errors.subscription_start_date.message}</Field.ErrorText>
                )}
              </Field.Root>
            </GridItem>

            <GridItem colSpan={2}>
              <Heading size="md" mt={6} color="main">Lieferadresse</Heading>
            </GridItem>

            <GridItem colSpan={{base: 2, md: 1}}>
              <Field.Root invalid={!!errors.title} mt={6}>
                <Field.Label htmlFor="title">Anrede</Field.Label>

                <Controller
                  control={control}
                  name="title"
                  render={({ field }) => (
                    <Select.Root
                      multiple={false}
                      name={field.name}
                      onValueChange={({ value }) => field.onChange(value[0])}
                      onInteractOutside={() => field.onBlur()}
                      collection={titles}
                      w="full"
                      deselectable
                    >
                      <Select.HiddenSelect />
                      <Select.Control>
                        <Select.Trigger>
                          <Select.ValueText placeholder="Anrede" />
                        </Select.Trigger>
                        <Select.IndicatorGroup>
                          <Select.Indicator />
                        </Select.IndicatorGroup>
                      </Select.Control>
                      <Portal>
                        <Select.Positioner>
                          <Select.Content>
                            {titles.items.map((titles) => (
                              <Select.Item item={titles} key={titles.value}>
                                {titles.label}
                                <Select.ItemIndicator />
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Positioner>
                      </Portal>
                    </Select.Root>
                  )}
                />

                {errors.title && (
                  <Field.ErrorText>{errors.title.message}</Field.ErrorText>
                )}
              </Field.Root>
            </GridItem>

            <GridItem colSpan={{base: 2, md: 1}}>
              <Field.Root required invalid={!!errors.firstname} mt={6}>
                <Field.Label htmlFor="firstname">
                  Vorname
                  <Field.RequiredIndicator />
                </Field.Label>

                <Input
                  id="firstname"
                  {...register("firstname", {
                    required: "Vorname ist erforderlich",
                    maxLength: {
                      value: 255,
                      message: "Vorname darf maximal 255 Zeichen lang sein",
                    },
                  })}
                  placeholder="Vorname"
                  type="text"
                />
                {errors.firstname && (
                  <Field.ErrorText>{errors.firstname.message}</Field.ErrorText>
                )}
              </Field.Root>
            </GridItem>

            <GridItem colSpan={{base: 2, md: 1}}>
              <Field.Root required invalid={!!errors.lastname} mt={6}>
                <Field.Label htmlFor="lastname">
                  Nachname
                  <Field.RequiredIndicator />
                </Field.Label>

                <Input
                  id="lastname"
                  {...register("lastname", {
                    required: "Nachname ist erforderlich",
                    maxLength: {
                      value: 255,
                      message: "Nachname darf maximal 255 Zeichen lang sein",
                    },
                  })}
                  placeholder="Nachname"
                  type="text"
                />
                {errors.lastname && (
                  <Field.ErrorText>{errors.lastname.message}</Field.ErrorText>
                )}
              </Field.Root>
            </GridItem>

            <GridItem colSpan={{base: 2, md: 1}}>
              <Field.Root required invalid={!!errors.address} mt={6}>
                <Field.Label htmlFor="address">
                  Adresse
                  <Field.RequiredIndicator />
                </Field.Label>

                <Input
                  id="address"
                  {...register("address", {
                    required: "Adresse ist erforderlich",
                    maxLength: {
                      value: 255,
                      message: "Adresse darf maximal 255 Zeichen lang sein",
                    },
                  })}
                  placeholder="Adresse"
                  type="text"
                />
                {errors.address && (
                  <Field.ErrorText>{errors.address.message}</Field.ErrorText>
                )}
              </Field.Root>
            </GridItem>

            <CityZipField namePrefix="" requiredPostcode={true} requiredCity={true} form={{ control, register, setValue, errors, clearErrors }} />

            <GridItem colSpan={{base: 2, md: 1}}>
              <Field.Root required invalid={!!errors.email} mt={6}>
                <Field.Label htmlFor="email">
                  E-Mail
                  <Field.RequiredIndicator />
                </Field.Label>

                <Input
                  id="email"
                  {...register("email", {
                    required: "E-Mail ist erforderlich",
                    maxLength: {
                      value: 255,
                      message: "E-Mail darf maximal 255 Zeichen lang sein",
                    },
                  })}
                  placeholder="E-Mail"
                  type="email"
                />
                {errors.email && (
                  <Field.ErrorText>{errors.email.message}</Field.ErrorText>
                )}
              </Field.Root>
            </GridItem>

            <GridItem colSpan={{base: 2, md: 1}}>
              <Field.Root invalid={!!errors.phone} mt={6}>
                <Field.Label htmlFor="phone">Telefonnummer</Field.Label>

                <Input
                  id="phone"
                  {...register("phone", {
                    minLength: {
                      value: 10,
                      message: "Telefonnummer ist nicht valid",
                    },
                    validate: (value) =>
                      isValidNumber(value) === true ||
                      "Telefonnummer hat nicht das korrekte Format: 012 345 67 89 | +41 12 345 67 89",
                  })}
                  placeholder="Telefonnummer"
                  type="phonenumber"
                />
                {errors.phone && (
                  <Field.ErrorText>{errors.phone.message}</Field.ErrorText>
                )}
              </Field.Root>
            </GridItem>
          </SimpleGrid>

          <Collapsible.Root open={open} onOpenChange={(e) => setOpen(e.open)}>
            <Collapsible.Trigger mt={6}>
              <Checkbox.Root>
                <Checkbox.HiddenInput />
                <Checkbox.Control _checked={{backgroundColor: "main"}} borderColor="main" />
                <Checkbox.Label>Abweichende Rechnungsadresse</Checkbox.Label>
              </Checkbox.Root>
            </Collapsible.Trigger>

            <Collapsible.Content>
              <SimpleGrid gap={5} columns={2}>
                <GridItem colSpan={{base: 2, md: 1}}>
                  <Field.Root invalid={!!errors.billing_title} mt={6}>
                    <Field.Label htmlFor="billing_title">Anrede</Field.Label>

                    <Controller
                      control={control}
                      name="billing_title"
                      render={({ field }) => (
                        <Select.Root
                          multiple={false}
                          name={field.name}
                          onValueChange={({ value }) => field.onChange(value[0])}
                          onInteractOutside={() => field.onBlur()}
                          collection={titles}
                          w="full"
                          deselectable
                        >
                          <Select.HiddenSelect />
                          <Select.Control>
                            <Select.Trigger>
                              <Select.ValueText placeholder="Anrede" />
                            </Select.Trigger>
                            <Select.IndicatorGroup>
                              <Select.Indicator />
                            </Select.IndicatorGroup>
                          </Select.Control>
                          <Portal>
                            <Select.Positioner>
                              <Select.Content>
                                {titles.items.map((titles) => (
                                  <Select.Item item={titles} key={titles.value}>
                                    {titles.label}
                                    <Select.ItemIndicator />
                                  </Select.Item>
                                ))}
                              </Select.Content>
                            </Select.Positioner>
                          </Portal>
                        </Select.Root>
                      )}
                    />

                    {errors.billing_title && (
                      <Field.ErrorText>{errors.billing_title.message}</Field.ErrorText>
                    )}
                  </Field.Root>
                </GridItem>

                <GridItem colSpan={{base: 2, md: 1}}>
                  <Field.Root invalid={!!errors.billing_firstname} mt={6}>
                    <Field.Label htmlFor="billing_firstname">Vorname</Field.Label>

                    <Input
                      id="billing_firstname"
                      {...register("billing_firstname", {
                        maxLength: {
                          value: 255,
                          message: "Vorname darf maximal 255 Zeichen lang sein",
                        },
                      })}
                      placeholder="Vorname"
                      type="text"
                    />
                    {errors.billing_firstname && (
                      <Field.ErrorText>{errors.billing_firstname.message}</Field.ErrorText>
                    )}
                  </Field.Root>
                </GridItem>

                <GridItem colSpan={{base: 2, md: 1}}>
                  <Field.Root invalid={!!errors.billing_lastname} mt={6}>
                    <Field.Label htmlFor="billing_lastname">Nachname</Field.Label>

                    <Input
                      id="billing_lastname"
                      {...register("billing_lastname", {
                        maxLength: {
                          value: 255,
                          message: "Nachname darf maximal 255 Zeichen lang sein",
                        },
                      })}
                      placeholder="Nachname"
                      type="text"
                    />
                    {errors.billing_lastname && (
                      <Field.ErrorText>{errors.billing_lastname.message}</Field.ErrorText>
                    )}
                  </Field.Root>
                </GridItem>

                <GridItem colSpan={{base: 2, md: 1}}>
                  <Field.Root invalid={!!errors.billing_address} mt={6}>
                    <Field.Label htmlFor="billing_address">Adresse</Field.Label>

                    <Input
                      id="billing_address"
                      {...register("billing_address", {
                        maxLength: {
                          value: 255,
                          message: "Adresse darf maximal 255 Zeichen lang sein",
                        },
                      })}
                      placeholder="Adresse"
                      type="text"
                    />
                    {errors.billing_address && (
                      <Field.ErrorText>{errors.billing_address.message}</Field.ErrorText>
                    )}
                  </Field.Root>
                </GridItem>

                <CityZipField namePrefix="billing_" requiredPostcode={false} requiredCity={false} form={{ control, register, setValue, errors, clearErrors }} />
              </SimpleGrid>
            </Collapsible.Content>
          </Collapsible.Root>

          <Field.Root mt={6}>
            <Checkbox.Root
              required
              checked={dsgChecked}
              onCheckedChange={(e) => setDsgChecked(!!e.checked)}
              data-testid="check-dsg"
            >
              <Checkbox.HiddenInput />
              <Checkbox.Control _checked={{backgroundColor: "main"}} borderColor="main" />
              <Checkbox.Label>Ich habe die <Link color="main" href="/datenschutz" target="_blank">Datenschutzerklärung</Link> und <Link color="main" href={agbLink ?? "/"} target="_blank">AGB</Link> gelesen und akzeptiert</Checkbox.Label>
            </Checkbox.Root>
          </Field.Root>

            <Flex mt={6}>
                <Text color="dim" fontSize="sm">Diese Website ist durch reCAPTCHA geschützt.</Text>
            </Flex>

          <Button
            mt={10}
            variant="primary"
            textTransform="uppercase"
            type="submit"
            loading={isSubmitting}
            disabled={!dsgChecked}
          >
            Bestellen
          </Button>
        </form>
      </Container>
    </>
  )
}